import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Rocket, Building2, GraduationCap, ArrowRight, Loader2, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Logo from "@/components/Logo";

// Curated feature bullets per plan (marketing plan §6). Live price/credits/caps come
// from GET /api/plans (the single source of truth), never hardcoded here.
const PLAN_META = {
  free: { icon: Sparkles, accent: "#64748b", tagline: "Start creating for free", features: [
    "Core AI creation tools", "Starter templates + basic assets", "Publish to GameView Arcade", "Community access", "Monthly AI credit allowance"] },
  pro: { icon: Rocket, accent: "#3b9eff", tagline: "For serious creators", popular: true, features: [
    "Everything in Free", "More monthly AI credits", "No GameView watermark", "Advanced templates + premium assets", "Creator analytics + monetisation tools", "Commercial publishing + version history"] },
  studio: { icon: Building2, accent: "#22c55e", tagline: "For teams & studios", features: [
    "Everything in Creator Pro", "Unlimited projects + larger storage", "Advanced AI play testing", "Priority rendering + priority support", "Team asset libraries", "Studio permissions"] },
};
const PLAN_ORDER = ["free", "pro", "studio"];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [currency, setCurrency] = useState("usd");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/billing/plans").then((r) => { setPlans(r.data.plans); setCurrency(r.data.currency || "usd"); })
      .catch(() => setPlans({}));
  }, []);

  const choose = async (pid) => {
    if (!user) { navigate("/register"); return; }
    if (pid === "free" || user.plan === pid) return;
    setBusy(pid);
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: pid, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setBusy(""); toast.error(e.response?.data?.detail || "Could not start checkout"); }
  };

  const fmtCap = (n) => (n < 0 ? "Unlimited" : n);

  return (
    <div className="min-h-screen bg-[#05060a] text-white" data-testid="pricing-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/40 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo to={user ? "/app" : "/"} size="md" animated />
          <Button variant="ghost" onClick={() => navigate(user ? "/app" : "/")} className="text-white/80" data-testid="pricing-back">
            <ArrowLeft className="w-4 h-4" /> {user ? "Dashboard" : "Home"}
          </Button>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-14 pb-6 text-center">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-3">Simple pricing. Serious creation.</h1>
        <p className="text-white/60 max-w-2xl mx-auto">Start free and upgrade when your games take off. Every plan includes the AI Game Builder, live preview and one-click publishing to the Arcade.</p>
      </section>

      {!plans ? (
        <div className="grid place-items-center py-20"><Loader2 className="w-6 h-6 animate-spin text-white/50" /></div>
      ) : (
        <section className="max-w-6xl mx-auto px-5 pb-8 grid md:grid-cols-3 gap-5">
          {PLAN_ORDER.filter((pid) => plans[pid]).map((pid) => {
            const p = plans[pid]; const meta = PLAN_META[pid]; const Icon = meta.icon;
            const isCurrent = user?.plan === pid;
            return (
              <div key={pid} data-testid={`plan-card-${pid}`}
                className={`relative rounded-3xl border p-6 flex flex-col ${meta.popular ? "border-[#3b9eff] bg-white/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
                {meta.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold px-3 py-1 rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]">MOST POPULAR</span>}
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: `${meta.accent}22` }}><Icon className="w-5 h-5" style={{ color: meta.accent }} /></span>
                  <h3 className="font-display text-xl font-bold">{p.name}</h3>
                </div>
                <p className="text-sm text-white/50 mb-4">{meta.tagline}</p>
                <div className="mb-1">
                  <span className="text-4xl font-extrabold">${Number(p.price).toFixed(p.price % 1 ? 2 : 0)}</span>
                  <span className="text-white/50 text-sm">{p.price > 0 ? " / month" : ""}</span>
                </div>
                <div className="text-xs text-[#3b9eff] font-semibold mb-4">{p.credits.toLocaleString()} AI credits / month</div>
                <ul className="space-y-2 mb-6 flex-1">
                  {meta.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white/80"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: meta.accent }} /> {f}</li>
                  ))}
                  <li className="flex items-start gap-2 text-sm text-white/60"><Check className="w-4 h-4 mt-0.5 shrink-0 text-white/40" /> {fmtCap(p.caps.max_games)} projects · {fmtCap(p.caps.max_published)} published{p.caps.marketplace ? " · Marketplace selling" : ""}</li>
                </ul>
                <Button onClick={() => choose(pid)} disabled={isCurrent || busy === pid} data-testid={`plan-cta-${pid}`}
                  className={`w-full rounded-full ${meta.popular ? "bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" : ""}`}
                  variant={meta.popular ? "default" : "outline"}>
                  {busy === pid ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? "Your current plan" : pid === "free" ? (user ? "Included" : "Start free") : <>Upgrade to {p.name} <ArrowRight className="w-4 h-4" /></>}
                </Button>
              </div>
            );
          })}
        </section>
      )}

      {/* Education — licences, not a self-serve plan (guardrail: not yet a checkout product) */}
      <section className="max-w-6xl mx-auto px-5 pb-16">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-violet-500/10 to-indigo-500/10 p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-testid="plan-card-education">
          <span className="w-14 h-14 rounded-2xl grid place-items-center bg-gradient-to-br from-violet-400 to-indigo-500 shrink-0"><GraduationCap className="w-7 h-7 text-white" /></span>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-2xl font-bold">GameView Education</h3>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-white/70 uppercase tracking-wide">Talk to us</span>
            </div>
            <p className="text-sm text-white/60 mt-1 max-w-2xl">Per-student licences, classroom packages and school-wide plans with a teacher dashboard, controlled student publishing, project templates and onboarding. Turn your classroom into a game studio.</p>
          </div>
          <Button onClick={() => navigate("/for/schools")} variant="outline" className="rounded-full border-white/20 text-white hover:bg-white/10 shrink-0" data-testid="plan-cta-education">
            Bring GameView to your school <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 pb-20 text-center">
        <p className="text-white/50 text-sm">Need more AI power? Every plan can top up anytime with credit packs.{user ? <> <button onClick={() => navigate("/app/credits")} className="text-[#3b9eff] font-semibold underline" data-testid="pricing-credits-link">Buy credits</button></> : null}</p>
      </section>
    </div>
  );
}
